/**
 * Reception desk — the brand "operations desk that feels like a guestbook".
 *
 * Three panes: the left rail comes from the AppShell; this module renders the
 * main column (editorial header → KPI cards → call-volume chart → recent-calls
 * ledger) as `ReceptionPage`, and the right rail (live call + AI draft
 * reservation) as `ReceptionAside` (mounted by HotelLayout on this route).
 *
 * Real KPI counts come from `useCalls` via the presentation seam; richer
 * surfaces (deltas, 14-day chart, live call, draft) are fixture-backed behind
 * `// SEAM →` until the backend exposes them. Provider-independent, i18n,
 * keyboard-reachable, calm states.
 */

import { useFormatters, useT } from '@hostesa/i18n';
import {
  ActionButton,
  Card,
  type ChartSeries,
  DraftReservationCard,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  LanguagePill,
  LineChartCard,
  LiveCallPanel,
  type LiveTurn,
  LoadingState,
  MetricCard,
  type RecentCallItem,
  RecentCallsList,
} from '@hostesa/ui';
import { Calendar, Plus } from 'lucide-react';
import { useMemo } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import { useCallVolume } from '../../lib/presentation/use-call-volume';
import { useDraftReservation } from '../../lib/presentation/use-draft-reservation';
import { useLiveCall } from '../../lib/presentation/use-live-call';
import { useRecentCalls } from '../../lib/presentation/use-recent-calls';
import { useReceptionDesk } from '../../lib/presentation/use-reception-desk';
import { languageLabel, shortId } from '../calls/components/call-presentation';
import {
  CallRoomSubscription,
  HotelRoomSubscription,
} from '../calls/components/realtime-subscriptions';

const MAIN_WRAP = 'flex flex-col gap-8';

/** Turn an IANA timezone into a human city, e.g. "Europe/Sofia" → "Sofia". */
function cityFromTimezone(tz: string): string {
  const last = tz.split('/').pop() ?? tz;
  return last.replace(/_/g, ' ');
}

export interface ReceptionPageProps {
  onViewAllCalls?: () => void;
  onSelectCall?: (callId: string) => void;
  onNewCallHandlingRule?: () => void;
  onNewServiceRoutingRule?: () => void;
  onThisWeek?: () => void;
}

interface NewRuleActionProps {
  label: string;
  callHandlingLabel: string;
  callHandlingDescription: string;
  serviceRoutingLabel: string;
  serviceRoutingDescription: string;
  onNewCallHandlingRule?: () => void;
  onNewServiceRoutingRule?: () => void;
}

/**
 * The "+ New rule" header action. Renders nothing when no rule type is
 * available, a direct-navigating button when exactly one is, and a dropdown
 * menu when both are. Availability is decided by the route (callbacks are
 * `undefined` when unavailable), so this stays purely presentational.
 */
function NewRuleAction({
  label,
  callHandlingLabel,
  callHandlingDescription,
  serviceRoutingLabel,
  serviceRoutingDescription,
  onNewCallHandlingRule,
  onNewServiceRoutingRule,
}: NewRuleActionProps) {
  const items = [
    onNewCallHandlingRule && {
      testid: 'reception-new-rule-call-handling',
      title: callHandlingLabel,
      description: callHandlingDescription,
      onSelect: onNewCallHandlingRule,
    },
    onNewServiceRoutingRule && {
      testid: 'reception-new-rule-service-routing',
      title: serviceRoutingLabel,
      description: serviceRoutingDescription,
      onSelect: onNewServiceRoutingRule,
    },
  ].filter(Boolean) as Array<{
    testid: string;
    title: string;
    description: string;
    onSelect: () => void;
  }>;

  const [only] = items;
  if (!only) return null;

  if (items.length === 1) {
    return (
      <ActionButton
        data-testid="reception-new-rule-btn"
        variant="primary"
        icon={<Plus aria-hidden="true" className="size-3.5" />}
        onClick={only.onSelect}
      >
        {label}
      </ActionButton>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ActionButton
          data-testid="reception-new-rule-btn"
          variant="primary"
          icon={<Plus aria-hidden="true" className="size-3.5" />}
        >
          {label}
        </ActionButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map((item) => (
          <DropdownMenuItem key={item.testid} data-testid={item.testid} onSelect={item.onSelect}>
            <div className="flex flex-col">
              <span className="font-medium">{item.title}</span>
              <span className="text-xs text-[var(--color-stone-mid)]">{item.description}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ReceptionPage({
  onViewAllCalls,
  onSelectCall,
  onNewCallHandlingRule,
  onNewServiceRoutingRule,
  onThisWeek,
}: ReceptionPageProps = {}) {
  const { t } = useT('reception');
  const { tenantId, hotelId, hotelName, timezone } = useHotelContext();
  const { formatDate } = useFormatters(timezone);

  const desk = useReceptionDesk(tenantId, hotelId, timezone);
  const volume = useCallVolume(tenantId, hotelId, timezone);
  const recent = useRecentCalls(tenantId, hotelId);

  const factualLine = useMemo(
    () =>
      t('factualLine', {
        date: formatDate(new Date().toISOString()),
        city: cityFromTimezone(timezone),
      }),
    [t, formatDate, timezone],
  );

  const chartSeries = useMemo<ChartSeries[]>(
    () =>
      (volume.data?.series ?? []).map((s) => ({
        ...s,
        label: t(`chart.${s.id}`, { defaultValue: s.id }),
      })),
    [volume.data, t],
  );

  const recentRows = useMemo<RecentCallItem[]>(
    () =>
      (recent.data ?? []).map((r) => ({
        ...r,
        // The backend has no guest identity yet — fill an honest label, never a name.
        name: r.name ?? t('recent.caller'),
        statusLabel: r.statusLabel
          ? t(`status.${r.statusLabel}`, { defaultValue: r.statusLabel })
          : undefined,
      })),
    [recent.data, t],
  );

  if (desk.isLoading) {
    return (
      <section data-testid="reception-page" className={MAIN_WRAP}>
        <LoadingState data-testid="reception-loading" title={t('states.loading')} />
      </section>
    );
  }
  if (desk.isError) {
    return (
      <section data-testid="reception-page" className={MAIN_WRAP}>
        <ErrorState
          data-testid="reception-error"
          title={t('states.error.title')}
          description={t('states.error.description')}
        />
      </section>
    );
  }
  const d = desk.data;
  if (!d) {
    return (
      <section data-testid="reception-page" className={MAIN_WRAP}>
        <EmptyState
          data-testid="reception-empty"
          title={t('metrics.empty.title')}
          description={t('metrics.empty.description')}
        />
      </section>
    );
  }

  return (
    <section data-testid="reception-page" className={MAIN_WRAP}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1
            data-testid="reception-greeting"
            className="font-[var(--font-display)] text-3xl font-medium tracking-tight text-[var(--color-charcoal)]"
          >
            {t('greeting', { hotel: hotelName ?? t('eyebrow') })}
          </h1>
          <p className="font-[var(--font-text)] text-sm text-[var(--color-stone-mid)]">
            {factualLine}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ActionButton
            data-testid="reception-this-week-btn"
            icon={<Calendar aria-hidden="true" className="size-3.5" />}
            onClick={onThisWeek}
          >
            {t('actions.thisWeek')}
          </ActionButton>
          <NewRuleAction
            label={t('actions.newRule')}
            callHandlingLabel={t('actions.newRuleMenu.callHandling')}
            callHandlingDescription={t('actions.newRuleMenu.callHandlingDescription')}
            serviceRoutingLabel={t('actions.newRuleMenu.serviceRouting')}
            serviceRoutingDescription={t('actions.newRuleMenu.serviceRoutingDescription')}
            onNewCallHandlingRule={onNewCallHandlingRule}
            onNewServiceRoutingRule={onNewServiceRoutingRule}
          />
        </div>
      </header>

      <div
        data-testid="reception-metrics"
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <MetricCard
          data-testid="metric-calls-handled"
          label={t('metrics.callsHandled.label')}
          value={d.callsHandled.value}
          delta={d.callsHandled.delta}
          trend={d.callsHandled.trend}
        />
        <MetricCard
          data-testid="metric-bookings-captured"
          label={t('metrics.bookingsCaptured.label')}
          value={d.bookingsCaptured.value}
          delta={d.bookingsCaptured.delta}
          trend={d.bookingsCaptured.trend}
        />
        <MetricCard
          data-testid="metric-avg-handling-time"
          label={t('metrics.avgHandlingTime.label')}
          value={d.avgHandlingTime.value}
          delta={d.avgHandlingTime.delta}
          trend={d.avgHandlingTime.trend}
        />
        <MetricCard
          data-testid="metric-languages-served"
          label={t('metrics.languagesServed.label')}
          value={d.languagesServed.value}
          delta={d.languagesServed.delta}
        >
          <div className="flex flex-wrap gap-1">
            {d.languagesServed.codes.map((c, i) => (
              <LanguagePill key={c} code={c} active={i < 3} />
            ))}
          </div>
        </MetricCard>
      </div>

      {chartSeries.length > 0 && (
        <LineChartCard
          data-testid="reception-chart"
          title={t('chart.title')}
          series={chartSeries}
          xLabels={volume.data?.xLabels}
        />
      )}

      <Card data-testid="reception-recent" className="p-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-[var(--font-display)] text-lg font-medium tracking-tight text-[var(--color-charcoal)]">
            {t('recent.title')}
          </h2>
          <ActionButton
            data-testid="reception-view-all-btn"
            variant="ghost"
            onClick={onViewAllCalls}
          >
            {t('recent.viewAll')} →
          </ActionButton>
        </div>
        {recentRows.length > 0 ? (
          <RecentCallsList
            rows={recentRows}
            onSelect={onSelectCall}
            data-testid="reception-recent-list"
          />
        ) : (
          <EmptyState
            data-testid="reception-recent-empty"
            title={t('recent.empty.title')}
            description={t('recent.empty.description')}
          />
        )}
      </Card>
    </section>
  );
}

export interface ReceptionAsideProps {
  onWhisper?: () => void;
  onTakeOver?: () => void;
  onConfirmDraft?: () => void;
  onHoldDraft?: () => void;
}

export function ReceptionAside({
  onWhisper,
  onTakeOver,
  onConfirmDraft,
  onHoldDraft,
}: ReceptionAsideProps = {}) {
  const { t } = useT('reception');
  const { tenantId, hotelId } = useHotelContext();
  const live = useLiveCall(tenantId, hotelId);
  const draft = useDraftReservation(tenantId, hotelId);

  const call = live.data;

  const turns = useMemo<LiveTurn[]>(
    () =>
      (call?.turns ?? []).map((turn) => ({
        id: turn.id,
        speaker: turn.speaker,
        speakerLabel: t(`live.speaker.${turn.speaker}`, { defaultValue: turn.speaker }),
        lang: turn.lang,
        text: turn.text,
      })),
    [call, t],
  );

  // Honest caller identity — the backend has no guest name/phone yet, so the
  // heading is a plain label and the meta line carries only real call facts.
  const caller = useMemo(
    () =>
      call
        ? {
            name: t('live.callerHeading'),
            meta: [
              t(`live.direction.${call.direction}`, { defaultValue: call.direction }),
              languageLabel(call.language, t('common.notAvailable')),
              shortId(call.callId),
            ].join(' · '),
          }
        : undefined,
    [call, t],
  );

  const draftRows = useMemo(
    () =>
      (draft.data?.rows ?? []).map((row) => ({
        label: t(`draft.fields.${row.label}`, { defaultValue: row.label }),
        value: row.value,
      })),
    [draft.data, t],
  );

  return (
    <div className="flex flex-col gap-5">
      {/* New incoming calls invalidate the calls list; the transcript of the
          active call streams over the call room. Both need the RealtimeProvider
          mounted by the hotel guard. */}
      <HotelRoomSubscription tenantId={tenantId} hotelId={hotelId} />
      {call && <CallRoomSubscription tenantId={tenantId} hotelId={hotelId} callId={call.callId} />}

      {live.isLoading ? (
        <LoadingState data-testid="reception-live-loading" title={t('live.states.loading')} />
      ) : live.isError ? (
        <ErrorState
          data-testid="reception-live-error"
          title={t('live.states.error.title')}
          description={t('live.states.error.description')}
        />
      ) : (
        <LiveCallPanel
          data-testid="reception-live-call"
          caller={caller}
          turns={turns}
          liveLabel={t('live.live')}
          lineLabel={t('live.line')}
          whisperLabel={t('live.whisper')}
          takeOverLabel={t('live.takeOver')}
          emptyLabel={t('live.empty')}
          onWhisper={onWhisper}
          onTakeOver={onTakeOver}
        />
      )}
      {draft.data && (
        <DraftReservationCard
          data-testid="reception-draft"
          title={t('draft.title')}
          statusLabel={t('draft.status')}
          rows={draftRows}
          confirmLabel={t('draft.confirm')}
          holdLabel={t('draft.hold')}
          onConfirm={onConfirmDraft}
          onHold={onHoldDraft}
        />
      )}
    </div>
  );
}
